/**
 * ClientHiddenCost Component
 * Section 2: The Hidden Cost to Consultants
 */

import React from 'react';

export const ClientHiddenCost: React.FC = () => {
  const scrollToForm = () => {
    const element = document.getElementById('execution-fit-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="bg-white py-20 md:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">

          {/* Headline */}
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-stone-900 mb-12 leading-tight">
            Execution decay is not a client problem — it's an infrastructure gap.
          </h2>

          {/* Content */}
          <div className="space-y-8 text-lg md:text-xl text-stone-700 leading-relaxed">

            <p className="text-stone-900 font-medium">
              Most execution and alignment engagements follow the same pattern:
            </p>

            <ul className="space-y-3 ml-6">
              <li className="flex items-start">
                <span className="text-stone-400 mr-3">•</span>
                <span>Leadership leaves aligned and motivated</span>
              </li>
              <li className="flex items-start">
                <span className="text-stone-400 mr-3">•</span>
                <span>Priorities are clear at the top</span>
              </li>
              <li className="flex items-start">
                <span className="text-stone-400 mr-3">•</span>
                <span>Day-to-day execution slowly drifts</span>
              </li>
            </ul>

            <p className="text-stone-900 font-medium pt-4">
              Then reality sets in:
            </p>

            <ul className="space-y-3 ml-6">
              <li className="flex items-start">
                <span className="text-stone-400 mr-3">•</span>
                <span>Progress updates move to slides and spreadsheets</span>
              </li>
              <li className="flex items-start">
                <span className="text-stone-400 mr-3">•</span>
                <span>Follow-ups become manual</span>
              </li>
              <li className="flex items-start">
                <span className="text-stone-400 mr-3">•</span>
                <span>Reviews turn subjective</span>
              </li>
              <li className="flex items-start">
                <span className="text-stone-400 mr-3">•</span>
                <span>Outcomes depend heavily on your continued presence</span>
              </li>
            </ul>

            <p className="pt-4">
              Over time, execution failure gets explained away as:
            </p>

            <ul className="space-y-3 ml-6">
              <li className="flex items-start">
                <span className="text-stone-400 mr-3">•</span>
                <span>"Lack of discipline"</span>
              </li>
              <li className="flex items-start">
                <span className="text-stone-400 mr-3">•</span>
                <span>"Culture issues"</span>
              </li>
              <li className="flex items-start">
                <span className="text-stone-400 mr-3">•</span>
                <span>"Leadership follow-through"</span>
              </li>
            </ul>

            <p className="pt-4">
              But the real issue is simpler:
            </p>

            <p className="text-2xl md:text-3xl font-display text-stone-900 pt-2">
              Execution has no operating system.
            </p>

          </div>

          {/* CTA */}
          <div className="mt-12">
            <button
              onClick={scrollToForm}
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-stone-900 border-2 border-stone-300 rounded-lg hover:border-stone-900 hover:bg-stone-50 transition-all duration-300"
            >
              See how consultants prevent execution decay
            </button>
          </div>

        </div>
      </div>
    </section>
  );
};
